import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import AuthLayout from "../../../Layout/AuthLayout";
import { loginUser, googleLogin, appleLogin } from "../../../services/User/Authentication";
import toast from "react-hot-toast";
import { GoogleIcon, AppleIcon } from "../../../components/OAuthIcons";
import { AppStoreBadge, GooglePlayBadge } from "../../../components/StoreBadges";
import { requestNotificationPermission } from "../../../services/notification";

const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI;

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const onGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        const res = await googleLogin({ access_token: tokenResponse.access_token });
        localStorage.setItem("access_token", res.access);
        localStorage.setItem("refresh_token", res.refresh);
        localStorage.setItem("user", JSON.stringify(res.user));
        if (res.user && res.user.subscription) {
          localStorage.setItem("subscription", JSON.stringify(res.user.subscription));
        } else {
          localStorage.removeItem("subscription");
        }

        // Automatically request and register FCM token after login
        try {
          await requestNotificationPermission();
        } catch (fcmError) {
          console.error("FCM registration failed:", fcmError);
        }

        toast.success("Login successful");
        navigate("/dashboard");
      } catch (err) {
        toast.error(err.error || "Google login failed");
      } finally {
        setLoading(false);
      }
    },
    onError: () => toast.error("Google login failed"),
  });

  const handleAppleLogin = useCallback(() => {
    const state = Math.random().toString(36).substring(2, 15);
    const authorizeUrl = `https://appleid.apple.com/auth/authorize` +
      `?client_id=${encodeURIComponent(APPLE_CLIENT_ID)}` +
      `&redirect_uri=${encodeURIComponent(APPLE_REDIRECT_URI)}` +
      `&response_type=code id_token` +
      `&response_mode=form_post` +
      `&scope=${encodeURIComponent("name email")}` +
      `&state=${encodeURIComponent(state)}`;

    // Open popup
    const width = 600;
    const height = 650;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    
    const popup = window.open(
      authorizeUrl,
      "apple-login-popup",
      `width=${width},height=${height},left=${left},top=${top},status=no,resizable=yes`
    );

    if (popup) {
      popup.focus();
    }
  }, []);

  const onAppleLogin = useCallback(async (appleData) => {
    setLoading(true);
    try {
      const res = await appleLogin(appleData);
      localStorage.setItem("access_token", res.access);
      localStorage.setItem("refresh_token", res.refresh);
      localStorage.setItem("user", JSON.stringify(res.user));
      if (res.user && res.user.subscription) {
        localStorage.setItem("subscription", JSON.stringify(res.user.subscription));
      } else {
        localStorage.removeItem("subscription");
      }

      // Automatically request and register FCM token after login
      try {
        await requestNotificationPermission();
      } catch (fcmError) {
        console.error("FCM registration failed:", fcmError);
      }

      toast.success("Login successful");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.error || "Apple login failed");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const handleAppleMessage = (event) => {
      if (event.data && typeof event.data === "object" && event.data.type === "apple-auth-callback") {
        const { id_token, user } = event.data;
        if (id_token) {
          // Parse user name and email from Apple user payload if present
          let firstName = "";
          let lastName = "";
          let email = "";
          if (user && typeof user === "object") {
            firstName = user.name?.firstName || "";
            lastName = user.name?.lastName || "";
            email = user.email || "";
          }

          onAppleLogin({
            identity_token: id_token,
            first_name: firstName,
            last_name: lastName,
            email: email
          });
        } else {
          toast.error("Apple authentication failed");
        }
      }
    };

    window.addEventListener("message", handleAppleMessage);
    return () => {
      window.removeEventListener("message", handleAppleMessage);
    };
  }, [onAppleLogin]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const trigger = params.get("trigger");
    const errorParam = params.get("error");
    const appleToken = params.get("apple_token");
    const appleUserStr = params.get("apple_user");

    if (errorParam) {
      navigate("/login", { replace: true });
      toast.error(errorParam);
    } else if (appleToken) {
      navigate("/login", { replace: true });
      let firstName = "";
      let lastName = "";
      let email = "";
      if (appleUserStr) {
        try {
          const userObj = JSON.parse(appleUserStr);
          firstName = userObj?.name?.firstName || "";
          lastName = userObj?.name?.lastName || "";
          email = userObj?.email || "";
        } catch (e) {
          console.error("Error parsing apple user from redirect:", e);
        }
      }
      onAppleLogin({
        identity_token: appleToken,
        first_name: firstName,
        last_name: lastName,
        email: email
      });
    } else if (trigger === "google") {
      navigate("/login", { replace: true });
      onGoogleLogin();
    } else if (trigger === "apple") {
      navigate("/login", { replace: true });
      handleAppleLogin();
    }
  }, [location.search, navigate, onAppleLogin, onGoogleLogin, handleAppleLogin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await loginUser(formData);
      localStorage.setItem("access_token", res.access);
      localStorage.setItem("refresh_token", res.refresh);
      localStorage.setItem("user", JSON.stringify(res.user));
      if (res.user && res.user.subscription) {
        localStorage.setItem("subscription", JSON.stringify(res.user.subscription));
      } else {
        localStorage.removeItem("subscription");
      }
      sessionStorage.setItem("userEmail", formData.email);

      // Automatically request and register FCM token after login
      try {
        await requestNotificationPermission();
      } catch (fcmError) {
        console.error("FCM registration failed:", fcmError);
      }

      toast.success("Login successful");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.error || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <AuthLayout>
      <h2 className="xl:text-[28px] md:text-2xl text-xl text-[var(--color-text)] mb-2">
        Welcome Back
      </h2>
      <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] mb-8">
        Log in to your account
      </p>

      <form
        onSubmit={handleLogin}
        className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-4 text-left"
      >
        <div className="mb-5">
          <label className="md:text-sm text-xs label-caps block mb-2" htmlFor="email">
            EMAIL
          </label>
          <input
            id="email"
            autoComplete="email"
            type="email"
            placeholder="you@example.com"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[var(--radius-md)] text-[var(--color-text)] xl:text-base md:text-sm text-xs outline-none"
            required
          />
        </div>

        <div className="mb-6">
          <label className="md:text-sm text-xs label-caps block mb-2" htmlFor="password">
            PASSWORD
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => handleChange("password", e.target.value)}
              className="w-full py-3 pl-4 pr-10 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[var(--radius-md)] text-[var(--color-text)] xl:text-base md:text-sm text-xs outline-none"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="flex items-center justify-center absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-transparent border-none text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs cursor-pointer"
            >
              {showPassword ? "🙈" : "👁"}
            </button>
          </div>
          <div className="flex justify-end items-center mt-2">
            <Link
              to="/forget-password"
              className="md:text-sm text-xs text-[var(--color-primary)] font-bold"
            >
              Forgot Password?
            </Link>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`w-full xl:text-base md:text-sm text-xs bg-[var(--color-primary)] text-black border-none p-3 rounded-[var(--radius-md)] font-extrabold uppercase tracking-[0.06em] ${loading ? "cursor-not-allowed" : "cursor-pointer"}`}
        >
          {loading ? "LOGGING IN..." : "LOG IN"}
        </button>
      </form>

      <div
        className="flex items-center gap-4 my-8 text-xs font-bold text-[var(--color-text-muted)] uppercase tracking-[0.08em]"
      >
        <div className="flex-1 h-px bg-[var(--color-border)]" />
        OR CONTINUE WITH
        <div className="flex-1 h-px bg-[var(--color-border)]" />
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => onGoogleLogin()}
          disabled={loading}
          className="flex items-center justify-center gap-3 p-3 bg-[var(--color-text)] text-black border-none rounded-[var(--radius-md)] xl:text-base md:text-sm text-xs font-semibold cursor-pointer"
        >
          <GoogleIcon size={18} /> Continue with Google
        </button>
        <button
          onClick={handleAppleLogin}
          className="flex items-center justify-center gap-3 p-3 bg-black text-[var(--color-text)] border border-[var(--color-border)] rounded-[var(--radius-md)] xl:text-base md:text-sm text-xs font-semibold cursor-pointer"
        >
          <AppleIcon size={18} /> Continue with Apple
        </button>
      </div>

      <div className="flex flex-col items-center mt-6 gap-2">
        <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] text-center font-semibold mb-2">
          New here? Sign up exclusively through our mobile app:
        </p>
        <div className="flex md:flex-row flex-col items-center justify-center gap-4">
          <Link
            to="https://play.google.com/store/apps/details?id=com.horserace.app&pcampaignid=web_share"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-transform duration-200 hover:scale-105 active:scale-95"
            aria-label="Get it on Google Play"
          >
            <GooglePlayBadge height={52} />
          </Link>
          <Link
            to="https://apps.apple.com/us/app/formguru-horserace/id6784553692"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-transform duration-200 hover:scale-105 active:scale-95"
            aria-label="Download on the App Store"
          >
            <AppStoreBadge height={52} />
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Login;